import { useEffect, useMemo, useRef, type ReactNode } from "react";
import { QueryClientProvider as TanstackQueryClientProvider } from "@tanstack/react-query";
import { useAuth } from "./AuthContext";
import { useOrg } from "./OrgContext";
import { createDashboardQueryClient } from "./QueryClientProvider";

interface OrgScopedQueryBoundaryProps {
  children: ReactNode;
  /** Test/embedding override. Production derives this from auth + org state. */
  scopeKey?: string;
}

export function dashboardQueryScopeKey(
  account: { id: string; is_platform_admin: boolean } | null,
  activeOrgId: string | null,
): string {
  if (!account) return "anonymous";
  if (activeOrgId) return `account:${account.id}:org:${activeOrgId}`;
  if (account.is_platform_admin) return `account:${account.id}:platform`;
  return `account:${account.id}:account-org`;
}

/**
 * Query-cache ownership boundary for all authenticated application pages.
 *
 * Query keys throughout dashboard-v2 intentionally describe resources rather
 * than duplicating transport tenancy in every screen config. This keyed
 * provider makes the effective account + organization part of cache identity
 * once, at the application boundary. The key also remounts query observers so
 * ListPage's keepPreviousData cannot display rows from the departed scope.
 */
export function OrgScopedQueryBoundary({
  children,
  scopeKey: scopeKeyOverride,
}: OrgScopedQueryBoundaryProps) {
  const { account } = useAuth();
  const { activeOrgId } = useOrg();
  const scopeKey = scopeKeyOverride ?? dashboardQueryScopeKey(account, activeOrgId);
  const client = useMemo(createDashboardQueryClient, [scopeKey]);
  const previousClientRef = useRef(client);

  useEffect(() => {
    const previousClient = previousClientRef.current;
    previousClientRef.current = client;
    if (previousClient !== client) {
      // The keyed provider below has already detached the old observers before
      // this effect runs. Dispose their requests/data so no tenant cache is
      // retained in memory after the scope transition.
      void previousClient.cancelQueries();
      previousClient.clear();
    }
  }, [client]);

  return (
    <TanstackQueryClientProvider key={scopeKey} client={client}>
      {children}
    </TanstackQueryClientProvider>
  );
}
